import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  selectIsAuthenticated,
  setUser,
  setTherapistProfile,
  setClinicProfile,
  PENDING_LOGIN_ERROR_KEY,
} from "../store/slices/authSlice";
import { signInWithCustomToken, signOut, deleteUser } from "firebase/auth";
import {
  auth,
  setupRecaptcha,
  sendVerificationCode,
  verifyCodeWithInfo,
  getTherapistWithClinic,
  checkTherapistExistsByPhone,
  checkTherapistExistsByEmail,
  signInWithEmailPassword,
  resetPassword,
  sendCustomOtp,
  verifyCustomOtp,
} from "../firebase";
import usePageSeo from "../hooks/usePageSeo";
import CustomClinikitButton from "../components/ui/CustomClinikitButton";
import PhoneInput from "../components/ui/PhoneInput";
import OTPInput from "../components/ui/OTPInput";
import AuthLayout from "../components/auth/AuthLayout";
import {
  ChatCircleDots,
  EnvelopeSimple,
  WarningCircle,
  ArrowRight,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import "./AuthPages.css";

function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  usePageSeo({
    title: "התחברות למערכת",
    description: "התחברו לחשבון CliniKit שלכם – מערכת ניהול קליניקות מתקדמת למטפלים בישראל. כניסה עם טלפון או אימייל.",
    path: "/login",
  });

  // "phone" or "email"
  const [loginMethod, setLoginMethod] = useState("phone");

  // Phone state
  const [phone, setPhone] = useState("");
  // Custom-OTP fallback: engaged only when Firebase Phone Auth SMS is blocked.
  const [usingCustomOtp, setUsingCustomOtp] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [countdown, setCountdown] = useState(0);

  // Email state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResetSent, setIsResetSent] = useState(false);

  // Shared state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const otpInputRef = useRef(null);
  const recaptchaContainerRef = useRef(null);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // Show a login error that was stashed before a forced sign-out (orphan Auth
  // account with no therapist profile — see handleVerifyCode / AuthProvider
  // CK-1008). The sign-in briefly flips isAuthenticated, which navigates away
  // from this page, so the message must survive the round trip.
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem(PENDING_LOGIN_ERROR_KEY);
      if (pending) {
        sessionStorage.removeItem(PENDING_LOGIN_ERROR_KEY);
        setError(pending);
      }
    } catch (e) {}
  }, []);

  // Setup reCAPTCHA when component mounts (only needed for phone)
  useEffect(() => {
    if (recaptchaContainerRef.current) {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          // Ignore errors during cleanup
        }
        window.recaptchaVerifier = null;
      }
      setupRecaptcha("login-recaptcha-container");
    }

    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          // Ignore errors during cleanup
        }
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSwitchMethod = (method) => {
    if (isLoading) return;
    setLoginMethod(method);
    setError("");
    setIsCodeSent(false);
    setUsingCustomOtp(false);
    setIsForgotPassword(false);
    setIsResetSent(false);
    setVerificationCode("");
    setCountdown(0);
    setPassword("");
    setShowPassword(false);
  };

  // =====================
  // PHONE HANDLERS
  // =====================

  const isPhoneValid = phone && phone.startsWith("+972") && phone.length === 13;

  const handleSendCode = async (e) => {
    e?.preventDefault();
    if (isSubmittingRef.current) return;

    if (!isPhoneValid) {
      setError("יש להזין מספר טלפון ישראלי תקין");
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    setError("");

    try {
      const therapistExists = await checkTherapistExistsByPhone(phone);
      if (!therapistExists) {
        setError("לא נמצא משתמש רשום עם מספר זה. אנא הירשמו תחילה.");
        return;
      }

      await sendVerificationCode(phone);
      setIsCodeSent(true);
      setCountdown(60);

      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error("[CK-1001] Error sending verification code:", {
        errorCode: "CK-1001",
        errorMessage: error.message,
        firebaseCode: error.code,
        phone: phone ? `${phone.slice(0, 6)}***` : null,
        timestamp: new Date().toISOString(),
      });
      if (error.code === "auth/invalid-phone-number") {
        setError("מספר טלפון לא תקין");
        return;
      }

      // Firebase blocked the SMS (abuse detection: too-many-requests or the
      // internal error-code:-39). Fall back to our own OTP over ActiveTrail —
      // silent to the user, same code-entry UI.
      const firebaseBlocked =
        error.code === "auth/too-many-requests" ||
        error.code?.includes("error-code:-39") ||
        error.message?.includes("error-code:-39");

      if (firebaseBlocked) {
        try {
          const res = await sendCustomOtp(phone, "login");
          if (res?.success) {
            setUsingCustomOtp(true);
            setIsCodeSent(true);
            setCountdown(60);
            setTimeout(() => otpInputRef.current?.focus(), 100);
            return;
          }
          console.error("[CK-1004] Custom OTP login fallback rejected:", {
            errorCode: "CK-1004",
            fallbackError: res?.error,
            timestamp: new Date().toISOString(),
          });
        } catch (fallbackErr) {
          console.error("[CK-1004] Custom OTP login fallback error:", {
            errorCode: "CK-1004",
            errorMessage: fallbackErr.message,
            timestamp: new Date().toISOString(),
          });
        }
        setError(
          "שירות ה-SMS זמנית אינו זמין. נסו להתחבר באמצעות מייל וסיסמא."
        );
      } else {
        setError("שגיאה בשליחת הקוד. נסו שוב.");
      }
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    if (isSubmittingRef.current) return;

    const code = verificationCode;
    if (code.length !== 6) {
      setError("יש להזין קוד בן 6 ספרות");
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    setError("");

    try {
      // Custom-OTP fallback or Firebase Phone Auth. Both yield a signed-in user.
      let user;
      let isNewAuthUser = false;
      if (usingCustomOtp) {
        const res = await verifyCustomOtp(phone, code, "login");
        if (!res?.success) {
          if (res?.error === "CODE_EXPIRED") {
            setError("הקוד פג תוקף. שלחו קוד חדש.");
          } else if (res?.error === "TOO_MANY_ATTEMPTS") {
            setError("יותר מדי ניסיונות. שלחו קוד חדש.");
          } else if (res?.error === "NOT_REGISTERED") {
            setError("לא נמצא משתמש רשום עם מספר זה. אנא הירשמו תחילה.");
          } else {
            setError("קוד אימות שגוי");
          }
          setIsLoading(false);
          isSubmittingRef.current = false;
          return;
        }
        const cred = await signInWithCustomToken(auth, res.customToken);
        user = cred.user;
      } else {
        const verified = await verifyCodeWithInfo(code);
        user = verified.user;
        isNewAuthUser = verified.isNewUser;
      }
      const data = await getTherapistWithClinic(user.uid);

      if (!data) {
        // Signed-in Auth account with no therapist doc. Typical cause: the Auth
        // user was deleted (console) while the therapist doc kept the phone, so
        // the pre-check passed and confirm() minted a brand-new uid. Leaving the
        // session alive traps the user behind the dashboard loader forever
        // (AuthProvider retries "no data" indefinitely). Clean up: delete the
        // orphan account if we just created it, otherwise sign out, and stash the
        // error so it survives the isAuthenticated → /dashboard → /login bounce.
        const message = "לא נמצא משתמש עם מספר טלפון זה. אנא הירשמו תחילה.";
        console.error("[CK-1008] Login: Auth user has no therapist profile — signing out:", {
          errorCode: "CK-1008",
          isNewAuthUser,
          usingCustomOtp,
          userId: user.uid,
          timestamp: new Date().toISOString(),
        });
        try {
          sessionStorage.setItem(PENDING_LOGIN_ERROR_KEY, message);
        } catch (e) {}
        try {
          localStorage.removeItem("ck_authed");
        } catch (e) {}
        if (isNewAuthUser) {
          try {
            await deleteUser(user); // also signs out
          } catch (delErr) {
            console.error("[CK-1008] Orphan Auth user delete failed, signing out instead:", {
              errorCode: "CK-1008",
              errorMessage: delErr.message,
              timestamp: new Date().toISOString(),
            });
            await signOut(auth).catch(() => {});
          }
        } else {
          await signOut(auth).catch(() => {});
        }
        setError(message);
        setIsLoading(false);
        return;
      }

      dispatch(
        setUser({
          uid: user.uid,
          phoneNumber: user.phoneNumber,
          email: data.therapist?.email,
        })
      );
      dispatch(setTherapistProfile(data.therapist));
      dispatch(setClinicProfile(data.clinic));
      navigate("/dashboard");
    } catch (error) {
      console.error("[CK-1002] Error verifying code:", {
        errorCode: "CK-1002",
        errorMessage: error.message,
        firebaseCode: error.code,
        phone: phone ? `${phone.slice(0, 6)}***` : null,
        timestamp: new Date().toISOString(),
      });
      let errorMessage = "שגיאה באימות הקוד. נסו שוב.";

      if (error.code === "auth/invalid-verification-code") {
        errorMessage = "קוד אימות שגוי";
      } else if (error.code === "auth/code-expired") {
        errorMessage = "הקוד פג תוקף. שלחו קוד חדש.";
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0 || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsLoading(true);
    setError("");

    try {
      // Already on the custom-OTP fallback — re-send via ActiveTrail directly
      // instead of retrying (and re-failing) Firebase first.
      if (usingCustomOtp) {
        const res = await sendCustomOtp(phone, "login");
        if (res?.success) {
          setCountdown(60);
          setVerificationCode("");
          setTimeout(() => otpInputRef.current?.focus(), 100);
        } else {
          setError("שגיאה בשליחת הקוד. נסו שוב.");
        }
        return;
      }

      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          // Ignore
        }
        window.recaptchaVerifier = null;
      }
      setupRecaptcha("login-recaptcha-container");

      await sendVerificationCode(phone);
      setCountdown(60);
      setVerificationCode("");
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error("[CK-1003] Error resending verification code:", {
        errorCode: "CK-1003",
        errorMessage: error.message,
        firebaseCode: error.code,
        phone: phone ? `${phone.slice(0, 6)}***` : null,
        timestamp: new Date().toISOString(),
      });
      setError("שגיאה בשליחת הקוד. נסו שוב.");
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleBackToPhone = () => {
    setIsCodeSent(false);
    setVerificationCode("");
    setError("");
    setCountdown(0);
    setUsingCustomOtp(false);
  };

  // =====================
  // EMAIL + PASSWORD HANDLERS
  // =====================

  const isEmailValid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailPasswordLogin = async (e) => {
    e?.preventDefault();
    if (isSubmittingRef.current) return;

    if (!isEmailValid) {
      setError("יש להזין כתובת מייל תקינה");
      return;
    }

    if (!password) {
      setError("יש להזין סיסמא");
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    setError("");

    try {
      const normalizedEmail = email.trim().toLowerCase();

      const therapistExists = await checkTherapistExistsByEmail(normalizedEmail);
      if (therapistExists !== true) {
        setError("כתובת המייל לא קיימת במערכת. צרו קשר עם התמיכה.");
        setIsLoading(false);
        isSubmittingRef.current = false;
        return;
      }

      await signInWithEmailPassword(normalizedEmail, password);
    } catch (error) {
      console.error("[CK-1006] Error in email password login:", {
        errorCode: "CK-1006",
        errorMessage: error.message,
        firebaseCode: error.code,
        timestamp: new Date().toISOString(),
      });

      let errorMessage;
      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        errorMessage = "סיסמא שגויה. נסו שוב או לחצו על \"שכחתי סיסמא\".";
      } else if (error.code === "auth/user-not-found") {
        errorMessage = "כתובת המייל לא נמצאה במערכת.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "כתובת מייל לא תקינה";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage =
          "החשבון ננעל זמנית עקב ניסיונות כושלים רבים. נסו שוב מאוחר יותר או אפסו את הסיסמא.";
      } else if (error.code === "auth/user-disabled") {
        errorMessage = "החשבון הושבת. צרו קשר עם התמיכה.";
      } else {
        errorMessage = "שגיאה בהתחברות. נסו שוב.";
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (isSubmittingRef.current) return;

    if (!isEmailValid) {
      setError("יש להזין כתובת מייל תקינה כדי לאפס סיסמא");
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    setError("");

    try {
      await resetPassword(email.trim().toLowerCase());
      setIsResetSent(true);
    } catch (error) {
      console.error("[CK-1009] Error sending password reset:", {
        errorCode: "CK-1009",
        errorMessage: error.message,
        firebaseCode: error.code,
        timestamp: new Date().toISOString(),
      });

      let errorMessage;
      if (error.code === "auth/user-not-found") {
        errorMessage = "כתובת המייל לא נמצאה במערכת.";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "נשלחו יותר מדי בקשות. נסו שוב מאוחר יותר.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "כתובת מייל לא תקינה";
      } else {
        errorMessage = "שגיאה בשליחת המייל. נסו שוב.";
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // =====================
  // HEAD TEXT & STAGE KEY
  // =====================

  const getTitle = () => {
    if (loginMethod === "phone" && isCodeSent) return "אימות קוד";
    if (isResetSent) return "המייל בדרך אליכם";
    if (isForgotPassword) return "איפוס סיסמא";
    return "התחברות למערכת";
  };

  const getSubtitle = () => {
    if (loginMethod === "phone") {
      if (isCodeSent) return `קוד אימות נשלח למספר ${phone}`;
      return "ברוכים השבים! הזינו את מספר הטלפון שלכם";
    }
    if (isResetSent) return "";
    if (isForgotPassword) return "הזינו את כתובת המייל שלכם ונשלח קישור לאיפוס סיסמא";
    return "ברוכים השבים! הזינו את פרטי ההתחברות שלכם";
  };

  // Drives the stage-in animation on every step change
  const stageKey =
    loginMethod === "phone"
      ? isCodeSent
        ? "phone-otp"
        : "phone-number"
      : isResetSent
        ? "reset-sent"
        : isForgotPassword
          ? "forgot"
          : "email-password";

  const subtitle = getSubtitle();

  const errorBanner = error ? (
    <div className="auth-error-banner" role="alert">
      <WarningCircle size={18} weight="fill" />
      <span>{error}</span>
    </div>
  ) : null;

  const footerLink = (
    <p>
      אין לכם חשבון עדיין? <Link to="/#lead-form">קבעו שיחת הדרכה</Link>
    </p>
  );

  return (
    <AuthLayout variant="login" footer={footerLink}>
      <div className="auth-head">
        <h1>{getTitle()}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>

      {/* Method Toggle */}
      {!isCodeSent && !isForgotPassword && !isResetSent && (
        <div className="login-method-toggle">
          <button
            type="button"
            className={`login-method-btn ${loginMethod === "phone" ? "active" : ""}`}
            onClick={() => handleSwitchMethod("phone")}
            disabled={isLoading}
          >
            <ChatCircleDots size={17} weight={loginMethod === "phone" ? "fill" : "regular"} />
            קוד ב-SMS
          </button>
          <button
            type="button"
            className={`login-method-btn ${loginMethod === "email" ? "active" : ""}`}
            onClick={() => handleSwitchMethod("email")}
            disabled={isLoading}
          >
            <EnvelopeSimple size={17} weight={loginMethod === "email" ? "fill" : "regular"} />
            מייל וסיסמא
          </button>
        </div>
      )}

      <div className="auth-form-stage" key={stageKey}>
        {/* ========== PHONE LOGIN ========== */}
        {loginMethod === "phone" && !isCodeSent && (
          <form onSubmit={handleSendCode} className="auth-form">
            <div className="form-group">
              <label>מספר טלפון</label>
              <PhoneInput value={phone} onChange={setPhone} error={error} />
            </div>

            <CustomClinikitButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading || !isPhoneValid}
            >
              {isLoading ? "שולח..." : "שלח קוד אימות"}
            </CustomClinikitButton>

            <div className="login-note">
              <p>התחברות מאובטחת באמצעות SMS</p>
            </div>
          </form>
        )}

        {/* ========== PHONE - CODE ENTRY ========== */}
        {loginMethod === "phone" && isCodeSent && (
          <form onSubmit={handleVerifyCode} className="auth-form">
            <div className="form-group">
              <label className="code-label">קוד אימות</label>
              <OTPInput
                ref={otpInputRef}
                value={verificationCode}
                onChange={(digits) => {
                  setVerificationCode(digits);
                  if (error) setError("");
                }}
                length={6}
                disabled={isLoading}
                error={error}
                autoFocus
              />
            </div>

            <div className="resend-section">
              {countdown > 0 ? (
                <span className="countdown">
                  שליחה חוזרת אפשרית בעוד {countdown} שניות
                </span>
              ) : (
                <button
                  type="button"
                  className="resend-button"
                  onClick={handleResendCode}
                  disabled={isLoading}
                >
                  שלח קוד חדש
                </button>
              )}
            </div>

            <CustomClinikitButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading || verificationCode.length !== 6}
            >
              {isLoading ? "מאמת..." : "התחבר"}
            </CustomClinikitButton>

            <button
              type="button"
              className="back-button"
              onClick={handleBackToPhone}
              disabled={isLoading}
            >
              <ArrowRight size={14} weight="bold" />
              חזרה להזנת מספר טלפון
            </button>
          </form>
        )}

        {/* ========== EMAIL + PASSWORD LOGIN ========== */}
        {loginMethod === "email" && !isForgotPassword && !isResetSent && (
          <form onSubmit={handleEmailPasswordLogin} className="auth-form">
            {errorBanner}

            <div className="form-group">
              <label>כתובת מייל</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="your@email.com"
                className="email-login-input"
                disabled={isLoading}
                dir="ltr"
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label>סיסמא</label>
              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="הזינו סיסמא"
                  className="email-login-input password-login-input"
                  disabled={isLoading}
                  dir="ltr"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-visibility-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? "הסתר" : "הצג"}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="forgot-password-link"
              onClick={() => {
                setIsForgotPassword(true);
                setError("");
                setPassword("");
                setShowPassword(false);
              }}
              disabled={isLoading}
            >
              שכחתי סיסמא
            </button>

            <CustomClinikitButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading || !isEmailValid || !password}
            >
              {isLoading ? "מתחבר..." : "התחבר"}
            </CustomClinikitButton>
          </form>
        )}

        {/* ========== FORGOT PASSWORD ========== */}
        {loginMethod === "email" && isForgotPassword && !isResetSent && (
          <form onSubmit={handleForgotPassword} className="auth-form">
            {errorBanner}

            <div className="form-group">
              <label>כתובת מייל</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder="your@email.com"
                className="email-login-input"
                disabled={isLoading}
                dir="ltr"
                autoComplete="email"
                autoFocus
              />
            </div>

            <CustomClinikitButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading || !isEmailValid}
            >
              {isLoading ? "שולח..." : "שלח מייל לאיפוס סיסמא"}
            </CustomClinikitButton>

            <button
              type="button"
              className="back-button"
              onClick={() => {
                setIsForgotPassword(false);
                setError("");
              }}
              disabled={isLoading}
            >
              <ArrowRight size={14} weight="bold" />
              חזרה להתחברות
            </button>
          </form>
        )}

        {/* ========== PASSWORD RESET SENT ========== */}
        {loginMethod === "email" && isResetSent && (
          <div className="auth-form email-sent-state">
            <div className="email-sent-icon">
              <PaperPlaneTilt size={26} weight="fill" />
            </div>
            <p className="email-sent-message">
              שלחנו מייל לאיפוס סיסמא לכתובת{" "}
              <strong dir="ltr">{email}</strong>
            </p>
            <p className="email-sent-hint">
              בדקו את תיבת הדואר הנכנס (וגם ספאם). לחצו על הקישור כדי לבחור
              סיסמא חדשה.
            </p>

            {errorBanner}

            <button
              type="button"
              className="resend-button"
              onClick={() => {
                setIsResetSent(false);
                setIsForgotPassword(false);
                setPassword("");
                setError("");
              }}
              disabled={isLoading}
            >
              חזרה להתחברות
            </button>
          </div>
        )}
      </div>

      {/* reCAPTCHA container - invisible */}
      <div id="login-recaptcha-container" ref={recaptchaContainerRef}></div>
    </AuthLayout>
  );
}

export default LoginPage;
